/**
 * SearchProfilesModal — slug-prefix profile search opened from the
 * navbar's magnifying-glass icon.
 *
 * User types (no leading @, no URL — just the slug). As they type
 * we run `profiles.searchPublic` reactively; each result row links
 * to `/p/<slug>`. Private profiles are filtered server-side so
 * nothing here exposes them.
 *
 * Lifecycle: caller owns `open`. Esc + backdrop click both close.
 * The input autofocuses on mount and the field clears every time
 * the modal re-opens, mirroring the Spotify search pattern in
 * Builder.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api as convexApi } from '../../../convex/functions/_generated/api';

type Props = {
  open: boolean;
  onClose: () => void;
};

export function SearchProfilesModal({ open, onClose }: Props) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [raw, setRaw] = useState('');

  // Reset + focus on every fresh open. Without the reset, reopening
  // the modal would show whatever the user typed last time.
  useEffect(() => {
    if (!open) return;
    setRaw('');
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [open]);

  // Escape closes; consistent with ConfirmModal / ShareProfileCardModal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // The user types whatever they want, but slugs are [a-z0-9-]+.
  // Lowercase + strip non-slug chars on the wire so a query like
  // "Kelvin V." doesn't break the backend regex check.
  const sanitized = useMemo(
    () => raw.toLowerCase().trim().replace(/[^a-z0-9-]/g, ''),
    [raw],
  );

  const results = useQuery(
    convexApi.profiles.searchPublic,
    open && sanitized.length > 0 ? { query: sanitized, limit: 8 } : 'skip',
  );

  const onPick = useCallback(
    (slug: string) => {
      onClose();
      // Defer the nav by a tick so the modal animates closed before
      // React Router unmounts our portal.
      setTimeout(() => navigate(`/p/${slug}`), 0);
    },
    [navigate, onClose],
  );

  if (!open) return null;

  const showEmpty = sanitized.length > 0 && results !== undefined && results.length === 0;
  const showLoading = sanitized.length > 0 && results === undefined;
  const showHint = sanitized.length === 0;

  return createPortal(
    <div style={backdrop} onClick={onClose}>
      <div
        style={card}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Search profiles by slug"
      >
        <div style={header}>
          <SearchGlyph size={18} color="var(--ink-3)" />
          <input
            ref={inputRef}
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder="Find a profile by their slug"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            style={input}
            onKeyDown={(e) => {
              // Enter on a single-match list jumps straight there.
              if (e.key === 'Enter' && results && results.length > 0) {
                e.preventDefault();
                onPick(results[0].slug);
              }
            }}
          />
          <button type="button" onClick={onClose} aria-label="Close" style={closeBtn}>×</button>
        </div>

        <div style={resultsBox}>
          {showHint && (
            <div style={hint}>
              Type a slug — like the part after <code style={code}>wydm.app/p/</code>.
            </div>
          )}
          {showLoading && <div style={hint}>Searching…</div>}
          {showEmpty && (
            <div style={hint}>
              No public profile matches <strong>{sanitized}</strong>.
            </div>
          )}
          {results && results.length > 0 && (
            <ul style={list}>
              {results.map((r) => (
                <li key={r.slug}>
                  <button
                    type="button"
                    onClick={() => onPick(r.slug)}
                    style={row}
                  >
                    {r.avatarUrl ? (
                      <img src={r.avatarUrl} alt="" style={avatar} />
                    ) : (
                      <div style={{ ...avatar, background: 'var(--coral)' }} />
                    )}
                    <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                      <div style={rowName}>{r.displayName}</div>
                      <div className="mono" style={rowSlug}>
                        wydm.app/p/{r.slug}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── small inline magnifying glass so the input shows the icon even
// before the user types. Kept inline to avoid an Icons.search import
// that would split the modal's deps.
function SearchGlyph({ size = 18, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

// ── styles ───────────────────────────────────────────────────────────

const backdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(43, 29, 20, 0.5)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  zIndex: 1100,
  padding: '10vh 16px 16px',
};

const card: React.CSSProperties = {
  background: 'var(--surface)',
  borderRadius: 18,
  width: '100%',
  maxWidth: 460,
  boxShadow: '0 28px 64px rgba(0,0,0,0.32)',
  overflow: 'hidden',
};

const header: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '12px 14px',
  borderBottom: '1px solid var(--line)',
};

const input: React.CSSProperties = {
  flex: 1,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontFamily: 'var(--font-ui)',
  fontSize: 15,
  color: 'var(--ink)',
  padding: '8px 0',
};

const closeBtn: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 28,
  border: 'none',
  background: 'var(--chip)',
  color: 'var(--ink-2)',
  fontSize: 18,
  lineHeight: 1,
  cursor: 'pointer',
};

const resultsBox: React.CSSProperties = {
  maxHeight: '60vh',
  overflowY: 'auto',
  padding: 6,
};

const hint: React.CSSProperties = {
  padding: '16px 14px',
  fontSize: 13,
  color: 'var(--ink-3)',
  lineHeight: 1.5,
};

const code: React.CSSProperties = {
  background: 'var(--chip)',
  padding: '0 6px',
  borderRadius: 4,
  fontFamily: 'var(--font-mono)',
  fontSize: 12,
};

const list: React.CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
};

const row: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 12px',
  width: '100%',
  background: 'transparent',
  border: 'none',
  borderRadius: 12,
  cursor: 'pointer',
  textAlign: 'left',
};

const avatar: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 40,
  objectFit: 'cover',
  flex: '0 0 40px',
};

const rowName: React.CSSProperties = {
  fontFamily: 'var(--font-ui)',
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--ink)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const rowSlug: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--ink-3)',
  marginTop: 2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
